import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { RootState } from '../../reducers';
import { Box, Tabs, Tab, Button, Grid, Snackbar, Alert, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../utils/dateHelpers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { fetchRoles, resetAssessmentError, resetAssessmentSuccess, setAssessmentSuccess, updateAssessmentMenu } from '../../store/assessment.slice';
import { roleEnum, assessmentMenuEnum, getBHDiagnosticAssessmentsList } from '../../utils/assessments';
import Consent from './Consent/Consent';
import './Assessment.css';
import { MenuDetails } from '../../Models/Assessment/AssessmentMenu.model';
import VitalSigns from './VitalSigns/VitalSigns';
import Sdoh from './Sdoh/SdohAssessment';
import PatientHealthQuestionnaire from './PHQ9/PHQ9';
import DiabetesFoot from './DiabetesFoot/DiabetesFoot';
import UrinalysisReport from './UrinalysisReport/UrinalysisReport';
import ProgressNotes from './ProgressNotes/ProgressNotes';
import ProcedureSignOff from './ProcedureSignOff/ProcedureSignOff';
import SetAppointment from './SetAppointment/SetAppointment';
import SummaryNotes from './SummaryNotes/SummaryNotes';
import MemberAllergyComponent from './MemberAllergy/MemberAllergy';
import CounselingComponent from './Counseling/Counseling';
import MiniMentalExamComponent from './MiniMentalExam/MiniMentalExam';
import MemberPcpSpecialist from '../Members/MemberDetail/MemberPcpSpecialist/MemberPcpSpecialist';
import SubjectiveAssessmentComponent from './SubjectiveAssessment/SubjectiveAssessment';
import OtherFactorAndRiskAssessment from './OtherFactorAndRiskAssessment/OtherFactorAndRiskAssessment';
import HgbA1C from './HgbA1C/HgbA1C';
import CarePlans from './CarePlans/CarePlans';
import LiqidAssessment from './LipidAssessment/LipidAssessment';
import { getMemberAssessmentHistoryForWorkflow, saveEngagementDraft } from '../../store/documentEngagement.slice';
import classes from './Styles';
import MemberAssessmentDemographics from './Demographics/Demographics';
import Interventions from './Interventions/Interventions';
import BudgetForm from './BudgetForm/BudgetForm';
import MemberAssessmentCareCircle from './CareCircle/CareCircle';
import { PrepareMenuClass } from '../../Models/Shared/PrepareMenuClass.model';
import TabletComponent from './Tablet/Tablet';
import FollowUp from './FollowUp/FollowUp';
import MedicationReconciliation from './MedicationReconciliation/MedicationReconciliation';
import RemoteMonitoring from './RemoteMonitoring/RemoteMonitoring';
import ActivitiesOfDailyLiving from './ActivitiesOfDailyLiving/ActivitiesOfDailyLiving';
import SexualHealthAssessment from './SexualHealth/SexualHealth';
import FallRiskAssessment from './FallRiskAssessment/FallRiskAssessment';
import VisionAndHearingAssessment from './VisionAndHearingAssessment/VisionAndHearingAssessment';
import TobaccoAndDrug from './TobaccoAndDrug/TobaccoAndDrug';
import PreventativeHealthAdviceAssessment from './PreventativeHealthAdvice/PreventativeHealthAdvice';
import MemberAdvancedDirectives from '../Members/MemberDetail/MemberAdvancedDirectives';
import HraEducationAssessment from './HraEducation/HraEducation';
import Requests from '../Requests/Requests';
import MemberEducation from '../MemberEducation/MemberEducation';
import AdditionalCHPCriteriaAssessment from './AdditionalCHPCriteria/AdditionalCHPCriteria';
import ScreeningFuhFumAssessment from './ScreeningFuhFum/ScreeningFuhFum';
import TransitionalCare from '../TransitionalCare/TransitionalCare';
import { useParams } from 'react-router-dom';
import HamiltonAnxietyScale from './HamiltonAnxietyScale/HamiltonAnxietyScale';
import AbuseHistory from './AbuseHistory/AbuseHistory';
import LegalHistory from './LegalHistory/LegalHistory';
import DevelopmentalHistory from './DevelopmentalHistory/DevelopmentalHistory';
import CulturalConsiderations from './CulturalConsiderations/CulturalConsiderations';
import EatingDisorder from './EatingDisorder/EatingDisorder';
import MentalHealthTreatmentHistory from './MentalHealthTreatmentHistory/MentalHealthTreatmentHistory';
import CurrentNeeds from './CurrentNeeds/CurrentNeeds';
import { DiagnosticType } from '../../Enum/DiagnosticType';
import GAFScale from './GAFScale/GAFScale';
import PhysicalHealth from './PhysicalHealth/PhysicalHealth';
import { SmartToy } from '@mui/icons-material';
import SuicidalHomicidalIdeation from './SuicidalHomicidalIdeation/SuicidalHomicidalIdeation';
import { getMemberBasicInfo } from '../../store/memberDetail.slice';
import { isEmptyAnswerValue, hasAiAnswerContent } from '../../utils/aiFlagHelpers';

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
    sx?: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`vertical-tabpanel-${index}`} aria-labelledby={`vertical-tab-${index}`} {...other}>
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: any) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export const isAIIcon = (answerValue?: unknown) => {
    if (typeof answerValue !== 'undefined') {
        if (answerValue && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
            const item = answerValue as Record<string, any>;
            if ('IsAIAnswer' in item || 'Options' in item || 'AnswerText' in item || 'Type' in item) {
                if (!hasAiAnswerContent(item)) {
                    return null;
                }
            } else if (isEmptyAnswerValue(answerValue)) {
                return null;
            }
        } else if (isEmptyAnswerValue(answerValue)) {
            return null;
        }
    }
    return (
        <Tooltip
            title="This answers was generated by AI using your session. If inaccurate, please enter correct value before continuing."
            slotProps={{ tooltip: { sx: { fontSize: '1em' } } }}
        >
            <SmartToy sx={{ alignSelf: 'center' }} fontSize="small" color="success" />
        </Tooltip>
    );
};

const Assessment = (props: any) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const memberId = props.memberId || params.memberId;
    const engagementDraftId = props.engagementDraftId || params.engagementDraftId;

    const [loadedEngagementDraftId, setLoadedEngagementDraftId] = useState<string | null>(null);
    const requestedEngagementDraftId = useRef<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
    const { roles, assessmentMenuList, selectedRole, error, success, miniMentalExam } = useSelector((state: RootState) => state.assessment);
    const { memberAssessmentHistory, engagementDraft, isOpenSavedDraftsModel } = useSelector((state: RootState) => state.documentEngagement);
    const selectedMenu = assessmentMenuList.find((a) => a.IsActive)?.Name;

    const isInHra = selectedRole === roleEnum.HRA;
    // Get the dynamic list of required BH Diagnostic assessments (conditionally includes Suicidal/Homicidal Ideation)
    const bhDiagnosticAssessmentsList = getBHDiagnosticAssessmentsList(miniMentalExam);
    const initialPage = useCallback(async () => {
        dispatch(fetchRoles());
        if (!engagementDraftId || requestedEngagementDraftId.current === engagementDraftId) {
            return;
        }

        requestedEngagementDraftId.current = engagementDraftId;
        try {
            await dispatch(getMemberAssessmentHistoryForWorkflow(memberId, engagementDraftId, selectedRole));
            setLoadedEngagementDraftId(engagementDraftId);
        } catch {
            requestedEngagementDraftId.current = null;
        }
    }, [dispatch, memberId, engagementDraftId, selectedRole]);

    useEffect(() => {
        if (loadedEngagementDraftId === engagementDraftId) {
            return;
        }
        void initialPage();
    }, [initialPage, engagementDraftId, loadedEngagementDraftId]);

    const isWorkflowLoaded = loadedEngagementDraftId === engagementDraftId;

    useEffect(() => {
        const el = document.getElementById('assessment-top');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [selectedMenu]);

    useEffect(() => {
        dispatch(getMemberBasicInfo(memberId));
    }, [dispatch, memberId]);

    const showLoadingIcon = !isWorkflowLoaded;

    const handleOnClickSaveDraft = () => {
        setIsSavingDraft(true);
        const updatedMemberAssessmentHistory = { ...memberAssessmentHistory, EngagementDraft: engagementDraft };
        dispatch(
            saveEngagementDraft(updatedMemberAssessmentHistory, false, () => {
                setIsSavingDraft(false);
                dispatch(
                    setAssessmentSuccess({
                        Show: true,
                        Message: 'Your draft is saved successfully',
                    })
                );
            })
        );
    };

    const handleChangeMenu = (event: React.ChangeEvent<{}>, newValue: string) => {
        dispatch(updateAssessmentMenu({ ActiveMenu: newValue } as PrepareMenuClass));
    };

    const renderTabLable = (menu: MenuDetails, index: Number) => {
        const errorStatus = menu.IsPending ? 'Pending' : menu.IsExpired ? 'Expired' : '';
        return (
            <div className="menu-item" key={`assessment-menu-item-${index}`}>
                <ArrowForwardIosIcon fontSize="small" className="arrow-icon" />
                {/* <span className="fill">{menu.DisplayName || menu.Name}</span> */}
                {engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic && bhDiagnosticAssessmentsList.includes(menu.Name) ? (
                    <span className="fill" style={{ fontWeight: 'bold' }}>
                        *{menu.DisplayName || menu.Name}
                    </span>
                ) : (
                    <span className="fill">{menu.DisplayName || menu.Name}</span>
                )}
                {(menu.IsPending || menu.IsExpired) && (
                    <Tooltip title={errorStatus}>
                        <ErrorIcon fontSize="small" />
                    </Tooltip>
                )}
                {menu.IsCompleted && (
                    <Tooltip title="Complete">
                        <CheckCircleIcon fontSize="small" />
                    </Tooltip>
                )}
            </div>
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {showLoadingIcon ? (
                <Box display="flex" justifyContent="center" sx={classes.loadingContainer}>
                    {isOpenSavedDraftsModel ? null : <CircularProgress />}
                </Box>
            ) : (
                <>
                    <Grid id="assessment-top" container spacing={2} p={2}>
                        {engagementDraft?.DiagnosticStatus?.DiagnosticType !== DiagnosticType.None ? (
                            <Grid size={12} textAlign={'center'}>
                                <Typography variant="body1" fontWeight={'bold'} color={'error.main'}>
                                    {engagementDraft?.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic ? 'BEHAVIORAL HEALTH DIAGNOSTIC' : ''}
                                </Typography>
                            </Grid>
                        ) : null}

                        <Grid size={12}>
                            <Stack columnGap={1} flexDirection="row" height="100%" justifyContent="space-between" alignItems="center" ml={'auto'}>
                                <div className="pr-2">
                                    <label className="pull-left mt-2 font-bold">Last Assessment Completed : </label>
                                    <div className="pull-left mt-1 ml-1 vital-sign-date">
                                        {(() => {
                                            const d = toDate(memberAssessmentHistory.LastAssessmentCompletedOn);
                                            return d && isValid(d);
                                        })()
                                            ? (() => {
                                                  const d = toDate(memberAssessmentHistory.LastAssessmentCompletedOn);
                                                  return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                              })()
                                            : '-'}
                                    </div>
                                </div>
                                <Typography variant="body1" className="mt-2">
                                    {memberAssessmentHistory?.EngagementDraft?.AssessedRole?.RoleName ?? ''}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    className="text-transform mt-0"
                                    onClick={handleOnClickSaveDraft}
                                    endIcon={isSavingDraft ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    Save Draft
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} p={2} paddingTop={0}>
                        <Grid size={2}>
                            <div className="assessment-tab">
                                <Tabs orientation="vertical" value={selectedMenu} onChange={handleChangeMenu}>
                                    {assessmentMenuList.map((menu, index) => {
                                        return (
                                            <Tab
                                                key={`${menu.Id}`}
                                                disabled={menu.IsDisabled}
                                                value={menu.Name}
                                                label={renderTabLable(menu, index)}
                                                {...a11yProps(menu.Name)}
                                                className={`text-transform ${menu.IsExpired && 'menu-error'}`}
                                            />
                                        );
                                    })}
                                </Tabs>
                            </div>
                        </Grid>
                        <Grid size={10}>
                            <div className="assmnt-tab-panel-container">
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.CONSENT}>
                                    <Consent memberId={memberId} isAssessment={true} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.DEMOGRAPHICS}>
                                    <MemberAssessmentDemographics memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.CARE_PLANS}>
                                    <CarePlans />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.VITAL_SIGNS}>
                                    <VitalSigns memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PRAPARE_SDOH}>
                                    <Sdoh memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.BUDGET_FORM}>
                                    <BudgetForm />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PHYSICAL_HEALTH}>
                                    <PhysicalHealth memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.HGBA1C_ASSESSMENT}>
                                    <HgbA1C memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.TRANSITIONAL_CARE}>
                                    <TransitionalCare memberId={memberId} isAssessment={true} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.EDUCATION}>
                                    <MemberEducation memberId={memberId} isAssessment={true} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.DEVELOPMENTAL_HISTORY}>
                                    <DevelopmentalHistory memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.CULTURAL_CONSIDERATIONS}>
                                    <CulturalConsiderations memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.EATING_DISORDER}>
                                    <EatingDisorder memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.MENTAL_HEALTH_TREATMENT_HISTORY}>
                                    <MentalHealthTreatmentHistory memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.CURRENT_NEEDS}>
                                    <CurrentNeeds memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.GAF_SCALE}>
                                    <GAFScale memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.MINI_MENTAL_STATUS_EXAM}>
                                    <MiniMentalExamComponent memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SUICIDAL_HOMICIDAL_IDEATION}>
                                    <SuicidalHomicidalIdeation memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SUBJECTIVE_ASSESSMENT}>
                                    <SubjectiveAssessmentComponent memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.ABUSE_HISTORY}>
                                    <AbuseHistory memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.LEGAL_HISTORY}>
                                    <LegalHistory memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.INTERVENTIONS}>
                                    <Interventions memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.OTHER_FACTORS_AND_RISKS}>
                                    <OtherFactorAndRiskAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PHQ9}>
                                    <PatientHealthQuestionnaire memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.TOBACCO_AND_DRUG}>
                                    <TobaccoAndDrug memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SEXUAL_HEALTH}>
                                    <SexualHealthAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.ADL}>
                                    <ActivitiesOfDailyLiving memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.FALL_RISK}>
                                    <FallRiskAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.VISION_AND_HEARING}>
                                    <VisionAndHearingAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PREVENTATION_HEALTH_ADVICE}>
                                    <PreventativeHealthAdviceAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.ADVANCED_DIRECTIVE}>
                                    <MemberAdvancedDirectives isAssessment />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.ADDITIONAL_CHP_CRITERIA}>
                                    <AdditionalCHPCriteriaAssessment memberId={memberId} />
                                </TabPanel>
                                {isInHra ? (
                                    <TabPanel value={selectedMenu} index={assessmentMenuEnum.HRA_EDUCATION}>
                                        <HraEducationAssessment memberId={memberId} />
                                    </TabPanel>
                                ) : null}
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.REQUESTS_REFERRALS}>
                                    <Requests memberId={memberId} isAssessment title={'CHP - Member Chart'} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.FOLLOW_UP}>
                                    <FollowUp memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.TABLETS}>
                                    <TabletComponent memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.LIPID_ASSESSMENT}>
                                    <LiqidAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.ALLERGIES}>
                                    <MemberAllergyComponent memberId={memberId} isAssessment={true} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.MEDICATION_RECONCILIATION}>
                                    <MedicationReconciliation memberId={memberId} memberDetail={false} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.DIABETES_FOOT}>
                                    <DiabetesFoot memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.URINALYSIS_REPORT}>
                                    <UrinalysisReport memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.REMOTE_MONITORING}>
                                    <RemoteMonitoring memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PCP_SPECIALIST}>
                                    <MemberPcpSpecialist isAssessment />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.CARE_CIRCLE} sx={classes.careCircleTab}>
                                    <MemberAssessmentCareCircle />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.COUNSELING}>
                                    <CounselingComponent memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SCREENING_FUHFUM}>
                                    <ScreeningFuhFumAssessment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.HAMILTON_ANXIETY_SCALE}>
                                    <HamiltonAnxietyScale memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PROGRESS_NOTES}>
                                    <ProgressNotes memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SET_APPOINTMENTS}>
                                    <SetAppointment memberId={memberId} />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.SUMMARY_NOTES}>
                                    <SummaryNotes />
                                </TabPanel>
                                <TabPanel value={selectedMenu} index={assessmentMenuEnum.PROCEDURE_SIGN_OFF}>
                                    <ProcedureSignOff memberId={memberId} />
                                </TabPanel>
                            </div>
                        </Grid>
                    </Grid>
                </>
            )}
            <Snackbar
                open={error?.Show}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => dispatch(resetAssessmentError())}
            >
                <Alert onClose={() => dispatch(resetAssessmentError())} severity="error" sx={{ width: '100%' }}>
                    {error?.Message}
                </Alert>
            </Snackbar>
            <Snackbar
                open={success?.Show}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => dispatch(resetAssessmentSuccess())}
            >
                <Alert onClose={() => dispatch(resetAssessmentSuccess())} severity="success" sx={{ width: '100%' }}>
                    {success?.Message}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
};

export default Assessment;
